import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { formatDistanceToNowStrict } from "date-fns";
import { ChevronDown, MessageSquare, Plus, Settings } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { AvatarRow } from "~/pages/manifold-nextgen/components/AvatarRow";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { UserAvatar } from "~/pages/manifold-nextgen/components/users/UserAvatar";
import { nextgenConversationPath, nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { Agent } from "~/services/agents";
import {
  deleteConversation as apiDeleteConversation,
  updateConversation as apiUpdateConversation,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";

interface SidebarSectionProps {
  title: string;
  onAdd?: () => void;
  addDisabled?: boolean;
  addLabel?: string;
  children: ReactNode;
}

const SidebarSection = ({ title, onAdd, addDisabled, addLabel, children }: SidebarSectionProps) => {
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <CollapsibleTrigger
          className={cn(
            "flex flex-1 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
            "outline-none focus-visible:text-foreground"
          )}
        >
          <ChevronDown
            className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
            aria-hidden="true"
          />
          {title}
        </CollapsibleTrigger>
        {addLabel && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground"
            onClick={onAdd}
            disabled={addDisabled || !onAdd}
            aria-label={addLabel}
          >
            <Plus className="size-3.5" />
          </Button>
        )}
      </div>
      <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
};

const EmptyHint = ({ children }: { children: ReactNode }) => (
  <p className="px-1 text-xs italic text-muted-foreground/80">{children}</p>
);

const RowSkeleton = ({ avatarRounded = "rounded-full" }: { avatarRounded?: string }) => (
  <li className="flex items-center gap-2 px-1 py-1">
    <Skeleton className={cn("size-5 shrink-0", avatarRounded)} />
    <Skeleton className="h-3 w-32" />
  </li>
);

const SkeletonList = ({ count, avatarRounded }: { count: number; avatarRounded?: string }) => (
  <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
    {Array.from({ length: count }).map((_, i) => (
      <RowSkeleton key={i} avatarRounded={avatarRounded} />
    ))}
  </ul>
);

export interface ProjectPerson {
  user_id: string;
  label: string;
}

interface ProjectSidebarProps {
  projectId: string;
  onSettings: () => void;
  onAddAgent: () => void;
  onAddDataset: () => void;
  onNewConversation: () => void;
  attachedAgents: Agent[] | undefined;
  attachedDatasets: Dataset[] | undefined;
  conversations: ProjectConversation[] | undefined;
  people: ProjectPerson[] | undefined;
}

interface ConversationRowProps {
  projectId: string;
  conversation: ProjectConversation;
  isRenaming: boolean;
  onStartRename: () => void;
  onEndRename: () => void;
  onShare: () => void;
  onDelete: () => void;
}

const ConversationRow = ({
  projectId,
  conversation,
  isRenaming,
  onStartRename,
  onEndRename,
  onShare,
  onDelete,
}: ConversationRowProps) => {
  const [draft, setDraft] = useState(conversation.display_name);
  const inputRef = useRef<HTMLInputElement>(null);

  // One-shot color flash when display_name changes (either via the rename
  // dropdown or a server-pushed conversation_name event). Tracking lives in
  // the row itself, which stays mounted across the rename mode toggle, so
  // the effect survives the input ↔ span swap.
  const [flashing, setFlashing] = useState(false);
  const prevNameRef = useRef(conversation.display_name);
  useEffect(() => {
    if (prevNameRef.current === conversation.display_name) return;
    prevNameRef.current = conversation.display_name;
    setFlashing(true);
    const id = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(id);
  }, [conversation.display_name]);

  useEffect(() => {
    if (isRenaming) {
      setDraft(conversation.display_name);
      // Defer focus + select so the input has mounted.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isRenaming, conversation.display_name]);

  const commit = () => {
    const next = draft.trim();
    const previous = conversation.display_name;
    onEndRename();
    if (!next || next === previous) return;
    // Optimistic: patch the cache so the row updates instantly. PATCH fires
    // in the background; revert the cache and revalidate from the server on
    // failure.
    const key = projectConversationsPath(projectId);
    const patch = (display_name: string) =>
      mutate(
        key,
        (current: { items: ProjectConversation[] } | undefined) =>
          current && {
            ...current,
            items: current.items.map((c) =>
              c.id === conversation.id ? { ...c, display_name } : c
            ),
          },
        { revalidate: false }
      );
    void patch(next);
    void apiUpdateConversation(conversation.id, { display_name: next }).catch(() => {
      void patch(previous);
      void mutate(key);
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  return (
    <li className="group relative cursor-pointer">
      <NavLink
        to={nextgenConversationPath(projectId, conversation.id)}
        className={({ isActive }) =>
          cn(
            "flex cursor-pointer items-center gap-2 rounded-md px-1 py-1",
            isActive
              ? "bg-[#ededed] dark:bg-neutral-700"
              : "hover:bg-[#f3f3f3] dark:hover:bg-neutral-800"
          )
        }
        onClick={(e) => {
          if (isRenaming) e.preventDefault();
        }}
      >
        {conversation.agent ? (
          <AgentAvatar
            id={conversation.agent.id}
            name={conversation.agent.name}
            size={20}
            aria-hidden="true"
          />
        ) : (
          <div
            className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground"
            aria-hidden="true"
          >
            <MessageSquare className="size-3.5" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.preventDefault()}
              aria-label="Rename conversation"
              className="w-full rounded border border-border bg-background px-1 py-0 text-xs font-semibold text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span
              className={cn(
                "truncate pr-6 text-xs font-semibold transition-colors duration-700",
                flashing ? "text-primary" : "text-foreground"
              )}
            >
              {conversation.display_name}
            </span>
          )}
          <span className="truncate text-[11px] text-muted-foreground">
            {formatDistanceToNowStrict(new Date(conversation.updated_at), { addSuffix: true })}
          </span>
        </div>
      </NavLink>

      {!isRenaming && (
        <ConversationActionsMenu onShare={onShare} onRename={onStartRename} onDelete={onDelete} />
      )}
    </li>
  );
};

type ActionTarget = { conversation: ProjectConversation; action: "share" | "delete" };

const ConversationsSection = ({
  projectId,
  conversations,
}: {
  projectId: string;
  conversations: ProjectConversation[];
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);

  const handleDelete = async () => {
    if (!actionTarget) return;
    const { conversation } = actionTarget;
    await apiDeleteConversation(conversation.id);
    await mutate(projectConversationsPath(projectId));
    const viewingThis = location.pathname.includes(`/c/${conversation.id}`);
    if (viewingThis) {
      navigate(nextgenProjectPath(projectId));
    }
  };

  const shareLink =
    actionTarget && typeof window !== "undefined"
      ? `${window.location.origin}${nextgenConversationPath(projectId, actionTarget.conversation.id)}`
      : "";

  return (
    <>
      <ul className="flex flex-col gap-0.5" role="list">
        {conversations.map((conv) => (
          <ConversationRow
            key={conv.id}
            projectId={projectId}
            conversation={conv}
            isRenaming={renamingId === conv.id}
            onStartRename={() => setRenamingId(conv.id)}
            onEndRename={() => setRenamingId(null)}
            onShare={() => setActionTarget({ conversation: conv, action: "share" })}
            onDelete={() => setActionTarget({ conversation: conv, action: "delete" })}
          />
        ))}
      </ul>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) setActionTarget(null);
        }}
        conversationTitle={actionTarget?.conversation.display_name ?? ""}
        link={shareLink}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) setActionTarget(null);
        }}
        conversationTitle={actionTarget?.conversation.display_name ?? ""}
        onConfirm={handleDelete}
      />
    </>
  );
};

export const ProjectSidebar = ({
  projectId,
  onSettings,
  onAddAgent,
  onAddDataset,
  onNewConversation,
  attachedAgents,
  attachedDatasets,
  conversations,
  people,
}: ProjectSidebarProps) => {
  return (
    <aside className="flex w-72 shrink-0 flex-col border-l bg-neutral-50 dark:bg-neutral-900">
      <div className="px-4 py-3">
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-primary hover:text-primary"
          onClick={onNewConversation}
          disabled={attachedAgents === undefined}
        >
          <Plus className="size-4" />
          New Conversation
        </Button>
      </div>

      <div className="flex flex-1 flex-col divide-y overflow-y-auto">
        <SidebarSection title="Conversations">
          {conversations === undefined ? (
            <SkeletonList count={2} />
          ) : conversations.length === 0 ? (
            <EmptyHint>No conversations yet.</EmptyHint>
          ) : (
            <ConversationsSection projectId={projectId} conversations={conversations} />
          )}
        </SidebarSection>

        <SidebarSection title="Datasets" addLabel="Manage datasets" onAdd={onAddDataset}>
          {attachedDatasets === undefined ? (
            <SkeletonList count={2} avatarRounded="rounded" />
          ) : attachedDatasets.length === 0 ? (
            <EmptyHint>No datasets attached.</EmptyHint>
          ) : (
            <ul className="flex flex-col gap-0.5" role="list">
              {attachedDatasets.map((dataset) => (
                <AvatarRow
                  key={dataset.id}
                  label={dataset.title}
                  avatar={<DatasetIcon dataset={dataset} size={20} className="rounded" />}
                />
              ))}
            </ul>
          )}
        </SidebarSection>

        <SidebarSection title="Agents" addLabel="Manage agents" onAdd={onAddAgent}>
          {attachedAgents === undefined ? (
            <SkeletonList count={2} />
          ) : attachedAgents.length === 0 ? (
            <EmptyHint>No agents attached.</EmptyHint>
          ) : (
            <ul className="flex flex-col gap-0.5" role="list">
              {attachedAgents.map((agent) => (
                <AvatarRow
                  key={agent.id}
                  label={agent.display_name}
                  avatar={<AgentAvatar id={agent.id} name={agent.name} size={20} />}
                />
              ))}
            </ul>
          )}
        </SidebarSection>

        <SidebarSection title="People" addLabel="Share project" addDisabled>
          {people === undefined ? (
            <SkeletonList count={1} />
          ) : people.length === 0 ? (
            <EmptyHint>No people yet.</EmptyHint>
          ) : (
            <ul className="flex flex-col gap-0.5" role="list">
              {people.map((person) => (
                <AvatarRow
                  key={person.user_id}
                  label={person.label}
                  avatar={<UserAvatar userId={person.user_id} size={20} />}
                />
              ))}
            </ul>
          )}
        </SidebarSection>
      </div>

      <div className="mt-auto border-t px-3 py-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={onSettings}
        >
          <Settings className="size-4" />
          Settings
        </Button>
      </div>
    </aside>
  );
};
